import React, { useContext, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ListOrders from "../../components/tables/ListOrders";
import Loader from "../../components/Loader";
import GenericAlert from "../../components/GenericAlert";
import FiltersHeaderContainer from "../../components/orders/FiltersHeaderContainer";
import { UserContext } from "../../contexts/userContext";
import { IAlert } from "../../interfaces/alert";
import {
  IMappedOrder,
  IOrder,
  IOrderRawWithMerchantName,
  IOrdersFilters,
  IOrdersListV2Response,
  IServiceType,
} from "../../interfaces/order";
import { IPaginationParams } from "../../interfaces/table";
import {
  ERROR,
  GET,
  INFO,
  MEDIUM,
  ROWS_PER_PAGE,
} from "../../utils/constants";
import { fetchData } from "../../utils/dataProvider";
import {
  buildOrdersV2Query,
  getFallenServicesMessage,
  getMappedInfo,
  mapLegacyOrdersFiltersToV2,
} from "../../utils/orders";
import { getStatusAliasList } from "../../requests/orderRequests";

const StatusChangesAudits = () => {
  const {
    currentUser: { entity },
    selectedMarketplace,
    rules,
  } = useContext(UserContext);

  const navigate = useNavigate();

  const [orders, setOrders] = useState<IMappedOrder[]>([]);
  const [pagination, setPagination] = useState<IPaginationParams>({ count: 0, page: 0 });
  const [alert, setAlert] = useState<IAlert>({} as IAlert);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<IOrdersFilters>({} as IOrdersFilters);
  const [aliases, setAliases] = useState<Record<string, string>>({});

  const fetchAliases = async (marketplaceId?: string) => {
    const aliasDict = await getStatusAliasList(marketplaceId);
    setAliases(aliasDict);
  };

  const getOrders = async (newPage = 0, nextFilters?: IOrdersFilters) => {
    const localFilters = nextFilters ?? filters;
    const v2Filters = mapLegacyOrdersFiltersToV2(localFilters, { page: newPage, limit: ROWS_PER_PAGE });
    const url = `/v2/orders${buildOrdersV2Query(entity, selectedMarketplace, v2Filters)}`;

    setLoading(true);
    setAlert({} as IAlert);
    try {
      const {
        results = [],
        count,
        fallen_services,
      } = await fetchData({ url, method: GET }) as IOrdersListV2Response;

      if (fallen_services?.length) {
        setAlert({ typeOfAlert: INFO, message: getFallenServicesMessage(fallen_services), showAlert: true } as IAlert);
      }

      if (results.length) {
        const legacyLike: IOrderRawWithMerchantName[] = results.map((r) => ({
          _id: r._id,
          carrier: r.carrier_name || "",
          client_id: r.client_id,
          createdAt: r.createdAt,
          customer_document: r.customer_document || "",
          customer_document_type: r.customer_document_type || "",
          external_id: r.external_id,
          hash: r.hash || "",
          merchant_id: r.merchant_id,
          orderInfo: (r.external_order ? r.external_order : {}) as unknown as IOrder,
          retries: r.retries,
          service: r.service as unknown as IServiceType,
          slaIsBroken: r.sla_is_broken ?? 0,
          sla_alert_level: r.sla_alert_level,
          status: r.status,
          status_history: r.status_history,
          tracking_id: r.tracking_id ?? "",
          updatedAt: r.updatedAt,
          merchant: r.merchant_name,
          merchant_name: r.merchant_name,
          alias: undefined,
          customization_fee: r.customization_fee,
          rejection_info: r.rejection_info || [],
          shipment_units: r.shipment_units || [],
        }));
        setOrders(getMappedInfo(legacyLike));
        setPagination({ count, page: newPage });
      } else {
        setOrders([]);
        setAlert({ typeOfAlert: INFO, message: "No hay órdenes para mostrar.", showAlert: true } as IAlert);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setAlert({ typeOfAlert: ERROR, message, showAlert: true });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => getOrders(newPage);

  const handleChangeFilter = (
    name: string,
    rawValue: string | number | null | undefined
  ) => {
    let value: string | null;
    if (rawValue === undefined || rawValue === null) value = null;
    else if (typeof rawValue === "number") value = String(rawValue);
    else value = rawValue as string;

    if (filters[name] !== value) setFilters({ ...filters, [name]: value });
    setPagination((p) => ({ ...p, page: 0 }));
    getOrders(0, { ...filters, [name]: value } as IOrdersFilters);
  };

  const handleSelectedAction = (action: string, order: IOrder) => {
    switch (action) {
    case "view-audit":
      navigate(`webhook/${order.trackingNumber}`);
      break;
    default:
      break;
    }
  };

  useEffect(() => {
    getOrders();
    fetchAliases(selectedMarketplace);
  }, [entity, selectedMarketplace]);

  let content = null as React.ReactNode;
  if (loading) content = <Loader size={MEDIUM} />;
  else if (alert.showAlert) content = <GenericAlert alert={alert} />;
  else
    content = (
      <ListOrders
        orders={orders}
        entity={entity}
        pagination={pagination}
        handlePageChange={handlePageChange}
        handleSelectedAction={handleSelectedAction}
        showActions={true}
        rules={rules}
      />
    );

  return (
    <Grid container>
      <Grid item xs={12}>
        <FiltersHeaderContainer
          handleChangeFilter={handleChangeFilter}
          setAlert={setAlert}
          aliases={aliases}
          availableFilters={[]}
        />
      </Grid>
      <Grid item xs={12}>{content}</Grid>
    </Grid>
  );
};

export default StatusChangesAudits;
